import * as React from 'react'
import { Outlet } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { observer } from 'mobx-react'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import { useFetchContractsPermissions } from 'modules/contracts/hooks/useFetchContractsPermissions'
import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractFlowProvider } from 'modules/contracts/lib/contractFlowContext'
import { ContractVersionsProvider } from 'modules/contracts/lib/contractVersionsContext'
import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import contractVersionsPayrollPolicies, {
  ContractVersionsPayrollPoliciesProvider,
} from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import type Employee from 'modules/employees/store/models/Employee'
import jobSalaryRangesCollection from 'modules/job_catalog/store/collections/salaryRanges'
import Page from 'modules/layout/components/Page'
import Loading from 'modules/layout/components/Page/Loading'
import policies, { PoliciesProvider } from 'modules/payroll_bundle/store/collections/policies'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  employee: Employee
}

export type ContractFlowType = 'fix' | 'promote' | 'update' | 'request_promote' | 'request_update'

const useFetchData = createAsyncData((employeeId: number, contractVersionId: number) => {
  return Promise.all([
    contractVersionsPayrollPolicies.fetch({
      data: {
        contract_version_ids: [contractVersionId],
      },
    }),
    policies.fetch({ data: { include_archived: true } }),
    contractVersions.fetch({
      data: {
        employee_ids: [employeeId],
        include_denormalized_data: true,
      },
    }),
    jobSalaryRangesCollection.fetch(),
  ])
})

const FlowPage = ({ employee }: Props) => {
  const params = useParams()
  const loadingContractData = useFetchData(params.id, params.contractVersionId)
  const { loading: loadingPermissions } = useFetchContractsPermissions(employee)
  const legalEntity = employeeLegalEntity(employee)
  const legalEntityContractData = useFetchLegalEntityContractData(
    legalEntity.id,
    legalEntity.country,
    true
  )

  const selectedContractVersion = contractVersions.get(Number(params.contractVersionId))
  const contractVersion =
    selectedContractVersion || employeeReferenceContract(contractVersions, employee)

  const isLoading = loadingContractData || loadingPermissions || legalEntityContractData.loading
  const canEditContract = policyAuthorizations.for('contracts.edit', employee)
  const canPromote = policyAuthorizations.for('contracts.promote', employee)
  const canAccessFlowsPage = canEditContract || canPromote
  const isNotFound = !!(
    !canAccessFlowsPage ||
    (!selectedContractVersion && params.contractVersionId)
  )
  const contractVersionPayrollPolicy = contractVersionsPayrollPolicies.isEmpty
    ? null
    : contractVersionsPayrollPolicies.models[0]!

  if (isLoading) return <Loading />

  return (
    <ContractFlowProvider
      employee={employee}
      contractVersion={contractVersion}
      salaryRanges={jobSalaryRangesCollection.models}
      contractVersionPayrollPolicy={contractVersionPayrollPolicy}
      legalEntityContractData={legalEntityContractData}
    >
      <ContractVersionsPayrollPoliciesProvider collection={contractVersionsPayrollPolicies}>
        <PoliciesProvider collection={policies}>
          <ContractVersionsProvider collection={contractVersions}>
            <Page
              isLoading={isLoading}
              notFound={{
                active: isNotFound && !isLoading,
                title: i18n.t('errors.page_not_found.title'),
                description: i18n.t('errors.page_not_found.description'),
              }}
            >
              <Outlet />
            </Page>
          </ContractVersionsProvider>
        </PoliciesProvider>
      </ContractVersionsPayrollPoliciesProvider>
    </ContractFlowProvider>
  )
}

export default observer(FlowPage)
